import React from 'react';
import snakeCase from 'lodash/snakeCase';
import { useTranslation } from 'common/i18n/hooks';
import { useQueryFeatureFlags } from 'iron/apps/experiments/src/components/hooks/query-feature-flag-list';
import { useStagedExperiment } from 'iron/apps/experiments/src/components/hooks/staged-experiment';
import { InputSize } from 'common/components/input/types';
import { InputReact } from 'common/components/input';
import { SelectReact } from 'common/components/select';
import { SelectTheme } from 'common/components/select/constants';
import { ButtonSize, ButtonTheme } from 'common/components/button/constants';
import { Section } from 'common/components/items-menu/types';
import { ChangeEvent } from 'common/components/select/types';
import { ButtonReact } from 'common/components/button';
import { ExperimentPageNewExperimentSetting } from '../_setting';
import styles from './index.module.styl';
export function CollectionMethodFeatureFlag() {
  var _stagedExperiment$set, _stagedExperiment$set2, _stagedExperiment$set3, _stagedExperiment$set4;
  const i18n = useTranslation();
  const {
    stagedExperiment,
    stageChanges
  } = useStagedExperiment();
  const {
    featureFlags,
    stagedFeatureFlag
  } = useQueryFeatureFlags();
  const [addNewFeatureFlag, setAddNewFeatureFlag] = React.useState(true);
  const [featureFlagKey, setFeatureFlagKey] = React.useState((stagedExperiment === null || stagedExperiment === void 0 || (_stagedExperiment$set = stagedExperiment.settings) === null || _stagedExperiment$set === void 0 || (_stagedExperiment$set = _stagedExperiment$set.featureFlagSettings) === null || _stagedExperiment$set === void 0 ? void 0 : _stagedExperiment$set.featureFlagKey) || "");
  const handleEnterExperimentName = (ev: React.ChangeEvent<HTMLInputElement>) => {
    const name = ev.target.value.trim();
    stageChanges({
      name,
      settings: {
        featureFlagSettings: {
          featureFlagId: undefined
        },
        controlKey: ""
      }
    });
    setFeatureFlagKey(snakeCase(name));
  };
  const handleSelectFeatureFlag = (ev: ChangeEvent) => {
    stageChanges({
      settings: {
        featureFlagSettings: {
          featureFlagId: ev.detail.item.value
        }
      }
    });
    setTimeout(() => {
      setFeatureFlagKey((stagedFeatureFlag === null || stagedFeatureFlag === void 0 ? void 0 : stagedFeatureFlag.key) || "");
    });
  };
  const usedFFKeys = React.useMemo(() => {
    return featureFlags.reduce((acc, flag) => {
      if (flag.key) {
        acc[flag.key] = true;
      }
      return acc;
    }, {} as Record<string, boolean>);
  }, [featureFlags]);
  const trimmedFeatureFlagKey = featureFlagKey.trim();
  const stageFeatureFlagKey = () => {
    if (!featureFlagKey || usedFFKeys[trimmedFeatureFlagKey]) {
      stageChanges({
        settings: {
          featureFlagSettings: {
            featureFlagKey: undefined
          }
        }
      });
    } else {
      stageChanges({
        settings: {
          featureFlagSettings: {
            featureFlagKey: trimmedFeatureFlagKey
          }
        }
      });
    }
  };
  React.useEffect(stageFeatureFlagKey, [trimmedFeatureFlagKey]);
  const featureFlagOptions: Section[] = [{
    items: featureFlags.filter(flag => !flag.experiment_id).map(flag => ({
      value: flag.id,
      label: flag.name,
      description: flag.name
    }))
  }];
  const ffKeyInUse = usedFFKeys[trimmedFeatureFlagKey];
  return <>
      <ExperimentPageNewExperimentSetting label={i18n.t("experiments.new.name")} data-sentry-element="ExperimentPageNewExperimentSetting" data-sentry-source-file="feature-flag.tsx">
        <InputReact className={styles.selectExperimentInput} value={stagedExperiment === null || stagedExperiment === void 0 || (_stagedExperiment$set2 = stagedExperiment.settings) === null || _stagedExperiment$set2 === void 0 || (_stagedExperiment$set2 = _stagedExperiment$set2.exposureEventSettings) === null || _stagedExperiment$set2 === void 0 ? void 0 : _stagedExperiment$set2.experimentName} size={InputSize.Large} placeholder={i18n.t("experiments.new.name-placeholder")} onInput={(ev: TODO) => handleEnterExperimentName(ev)} data-sentry-element="InputReact" data-sentry-source-file="feature-flag.tsx" />
      </ExperimentPageNewExperimentSetting>
      {Boolean(stagedExperiment === null || stagedExperiment === void 0 ? void 0 : stagedExperiment.name) && <ExperimentPageNewExperimentSetting label={i18n.t("experiments.new.feature-flag")} moreInfo={addNewFeatureFlag && !ffKeyInUse ? i18n.t("experiments.new.feature-flag-more-info") : undefined} errorInfo={usedFFKeys[trimmedFeatureFlagKey] ? i18n.t("experiments.new.feature-flag-already-exists") : undefined}>
          {addNewFeatureFlag ? <InputReact className={styles.selectExperimentInput} value={featureFlagKey} size={InputSize.Large} placeholder={i18n.t("experiments.new.add-experiment-placeholder")} onInput={(ev: TODO) => setFeatureFlagKey(ev.target.value.trim())} /> : <SelectReact className={styles.selectExperimentInput} selectedOption={(_stagedExperiment$set3 = stagedExperiment === null || stagedExperiment === void 0 || (_stagedExperiment$set4 = stagedExperiment.settings) === null || _stagedExperiment$set4 === void 0 || (_stagedExperiment$set4 = _stagedExperiment$set4.featureFlagSettings) === null || _stagedExperiment$set4 === void 0 ? void 0 : _stagedExperiment$set4.featureFlagId) !== null && _stagedExperiment$set3 !== void 0 ? _stagedExperiment$set3 : ""} label={(stagedFeatureFlag === null || stagedFeatureFlag === void 0 ? void 0 : stagedFeatureFlag.key) || i18n.t("experiments.new.feature-flag-placeholder")} theme={SelectTheme.Select} size={ButtonSize.Large} never-show-caret menuWidth={-1} onChange={(ev: TODO) => handleSelectFeatureFlag(ev)} sections={featureFlagOptions} />}

          <ButtonReact className={styles.experimentInputTypeBtn} theme={ButtonTheme.Secondary} size={ButtonSize.Small} icon={addNewFeatureFlag ? "search" : "plus"} onClick={() => setAddNewFeatureFlag(prev => !prev)}>
            {addNewFeatureFlag ? i18n.t("experiments.new.find-feature-flag") : i18n.t("experiments.new.add-feature-flag")}
          </ButtonReact>
        </ExperimentPageNewExperimentSetting>}
    </>;
}